import React, { useState } from "react";
import { Bell, BellOff, BellRing, Send, Loader2, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { toast } from "sonner";

export default function NotificationsButton() {
  const { supported, permission, subscribed, loading, enable, disable, sendTest } = usePushNotifications();
  const [open, setOpen] = useState(false);

  if (!supported) return null;

  const isBlocked = permission === "denied";
  const Icon = subscribed ? BellRing : isBlocked ? BellOff : Bell;
  const label = subscribed ? "Notifications on" : isBlocked ? "Blocked" : "Notifications";

  const onEnable = async () => {
    try {
      await enable();
      toast.success("Notifications enabled");
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    }
  };

  const onDisable = async () => {
    try {
      await disable();
      toast.success("Notifications disabled");
    } catch (e) {
      toast.error(e.message || "Could not disable");
    }
  };

  const onTest = async () => {
    try {
      const res = await sendTest();
      if (res.delivered > 0) toast.success(`Test push sent (${res.delivered})`);
      else toast.warning("No push delivered — check that your browser allows notifications");
    } catch (e) {
      toast.error("Test push failed");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-button"
          aria-label={label}
          className="relative inline-flex items-center justify-center border border-zinc-300 h-9 w-9 sm:h-10 sm:w-10 hover:bg-black hover:text-white hover:border-black transition-colors"
        >
          <Icon className="h-4 w-4" strokeWidth={1.75} />
          {subscribed && (
            <span className="absolute -top-1 -right-1 h-2 w-2 bg-emerald-500 border border-white" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-none border-zinc-300" align="end" data-testid="notifications-popover">
        <div className="p-4 border-b border-zinc-200">
          <div className="label-eyebrow">Notifications</div>
          <div className="font-display font-black text-lg tracking-tight leading-tight mt-1">
            {subscribed ? "You'll get pinged" : "Stay in the loop"}
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            {subscribed
              ? "We'll notify you when your tickets move between statuses — even when the app is closed."
              : "Get an on-device notification whenever your ticket status changes."}
          </div>
        </div>

        {isBlocked && !subscribed && (
          <div className="p-4 border-b border-zinc-200 bg-amber-50 text-xs text-amber-900" data-testid="push-blocked-notice">
            Notifications are blocked in your browser settings. Allow them for this site to turn on.
          </div>
        )}

        <div className="p-4 space-y-2">
          {subscribed ? (
            <>
              <Button
                onClick={onTest}
                disabled={loading}
                data-testid="push-test-btn"
                className="w-full rounded-none bg-black hover:bg-zinc-800 h-10 uppercase tracking-wider text-xs font-semibold justify-center gap-2"
              >
                <Send className="h-3.5 w-3.5" /> Send Test
              </Button>
              <Button
                onClick={onDisable}
                disabled={loading}
                variant="outline"
                data-testid="push-disable-btn"
                className="w-full rounded-none border-zinc-300 hover:bg-zinc-100 h-10 uppercase tracking-wider text-xs font-semibold justify-center gap-2"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Turn Off
              </Button>
            </>
          ) : (
            <Button
              onClick={onEnable}
              disabled={loading || isBlocked}
              data-testid="push-enable-btn"
              className="w-full rounded-none bg-black hover:bg-zinc-800 h-10 uppercase tracking-wider text-xs font-semibold justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
              Enable Notifications
            </Button>
          )}
        </div>

        <div className="px-4 py-3 border-t border-zinc-200 text-[10px] text-zinc-500 leading-relaxed">
          Tip: On iPhone, first install the app to your Home Screen. Notifications work in installed PWAs on iOS 16.4 and later.
        </div>
      </PopoverContent>
    </Popover>
  );
}
